import forge from 'node-forge'
import { decryptVaultBlob } from './format'

/**
 * **역방향 변환 — v2(AES-GCM) 금고 값을 v1(AES-CBC)로 되돌린다** (PR 5b).
 *
 * 설계: `backend/docs/design/vault_reverse_sweep/spec.md`
 *
 * 로드맵 §6-③ 이 *"문제를 발견하는 시점에는 이미 도구가 있어야 한다 … '롤백 계획'이 아니라
 * '롤백 도구'가 있어야 한다"* 고 못박은 것이 이 함수다. PR 6 이 신규 쓰기를 v2 로 전환하기
 * **전에** 완성돼야 하며, 그 시점 이후 문제가 드러나면 이미 v2 로 쓰인 데이터가 존재한다.
 *
 * 🛑 **순회 루프는 여기 없다** (설계 D8). 커서·청크·언락 훅은 PR 7 이 만들고, 그때
 * 정방향(v1 → v2)과 **같은 코드에 방향만 뒤집어** 쓴다. 여기서 만들면 그 순회와 중복된다.
 *
 * 🛑 **`pUtils` 를 import 하지 않는다.** `pUtils.js` 가 이미 `vaultWriteFormat` 을 부르고 있어
 * (PR 5a) 양방향이 되면 순환이다. v1 암호화를 **thunk 로 주입**받는다 —
 * `format.js:216 decryptVaultBlob(blob, keyMaterial, decryptV1Thunk)` 과 같은 관용구다.
 */

/**
 * "이건 v2 가 아니다" 를 판독기에게서 돌려받기 위한 표식.
 *
 * `decryptVaultBlob` 은 v2 가 아니면 **구포맷 thunk 를 부른다**(`format.js:232`). 우리는 구포맷을
 * 복호할 이유가 없으므로(이미 v1 이면 되돌릴 것이 없다) thunk 에서 이 표식을 던지고,
 * 그것이 되돌아오면 "대상 아님" 으로 판정한다. 판독기의 계약을 그대로 쓰는 것이라
 * 매직·길이·버전 판별 로직을 **여기서 복제하지 않는다**.
 */
const NOT_VAULT_V2 = Symbol('NOT_VAULT_V2')

/**
 * v2 blob 을 v1 로 다시 쓴다.
 *
 * @param {string} blobBase64  DB 에 저장된 그대로의 값 (base64 — 대상 4개 컬럼 전부 base64 다,
 *                             로드맵 §7.7 이 인코딩 지점을 컬럼별로 실측했다)
 * @param {string} keyMaterial v2 판독에 쓸 키 재료 (byte string)
 * @param {(plainByteString: string) => Promise<string>} encryptV1Thunk
 *                             평문을 v1 로 암호화해 **byte string** 을 돌려주는 함수.
 *                             호출부가 `encryptAESByKey(p, key)` 를 넘긴다 (`pUtils.js:96`).
 * @returns {Promise<string|null>} v1 blob 의 base64, 또는 **대상이 아니면 `null`**
 *
 * **복호 실패는 던진다** — 설계 F6/F7. 실패한 값을 그대로 쓰면 쓰레기가 저장되고,
 * 그것은 이 마이그레이션에서 되돌릴 수 없는 유일한 실패다.
 */
export const rewriteV2ToV1 = async (blobBase64, keyMaterial, encryptV1Thunk) => {
  const blobByteString = forge.util.decode64(blobBase64)

  let plain
  try {
    plain = await decryptVaultBlob(blobByteString, keyMaterial, () => {
      throw NOT_VAULT_V2
    })
  } catch (e) {
    if (e === NOT_VAULT_V2) {
      // v2 가 아니다 — 이미 v1 이거나 별개 네임스페이스의 포맷이다(로드맵 §10③).
      // 되돌릴 것이 없으므로 **건드리지 않는다.**
      return null
    }
    throw e
  }

  const v1ByteString = await encryptV1Thunk(plain)
  return forge.util.encode64(v1ByteString)
}

export default rewriteV2ToV1
